import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { StatusBadgeComponent } from '../../../shared/components/status-badge/status-badge.component';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import {
  Termo,
  TermoArquivoUpload,
  TermoArquivoSolicitado,
  TermoEdicao,
  TermoNovo,
  TermoStatus,
} from '../models/termo.model';
import { formatarTamanhoArquivo } from '../utils/tamanho-arquivo.util';
import { compararVersoes } from '../utils/versao.util';

/** Mesmo tamanho de página da lista de termos e das funcionalidades. */
const HISTORICO_PAGE_SIZE = 4;

const TAMANHO_MAXIMO_MB = 10;
const TAMANHO_MAXIMO_BYTES = TAMANHO_MAXIMO_MB * 1024 * 1024;

/** Obrigatório que, ao contrário do `Validators.required`, não aceita só espaços. */
function obrigatorio(control: AbstractControl): ValidationErrors | null {
  return String(control.value ?? '').trim() ? null : { required: true };
}

/** Compara só o que este painel exibe; ignora os vínculos, que são do painel ao lado. */
function mesmosDadosDoPainel(a: Termo, b: Termo): boolean {
  return (
    a.id === b.id &&
    a.nome === b.nome &&
    a.descricao === b.descricao &&
    a.versao === b.versao &&
    a.dataVersao === b.dataVersao &&
    a.status === b.status &&
    a.arquivo === b.arquivo &&
    a.historico === b.historico
  );
}

/**
 * Painel central: dados, PDF e histórico do termo, em visualização, alteração ou
 * criação. Não chama a API: emite eventos e a página trata.
 */
@Component({
  selector: 'app-termo-detalhe-panel',
  standalone: true,
  imports: [ReactiveFormsModule, StatusBadgeComponent, DataTableComponent, PaginationComponent],
  templateUrl: './termo-detalhe-panel.component.html',
  styleUrl: './termo-detalhe-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermoDetalhePanelComponent {
  private readonly fb = inject(FormBuilder);

  readonly termo = input.required<Termo>();
  readonly modoCriacao = input(false);

  readonly salvar = output<TermoEdicao>();
  readonly criar = output<TermoNovo>();
  readonly cancelarCriacao = output<void>();

  /** Só na criação: a página confirma antes de remover o PDF. */
  readonly removerArquivo = output<void>();

  readonly arquivoRecusado = output<string>();

  /** Com `url` abre direto; com `idVersao` a página baixa da API antes. */
  readonly visualizarArquivo = output<TermoArquivoSolicitado>();

  /** PDF escolhido e ainda não enviado. `model` porque a página também o limpa. */
  readonly arquivoNovo = model<TermoArquivoUpload | null>(null);

  readonly arquivoInvalido = signal(false);
  readonly alterando = signal(false);
  readonly exibindoFormulario = computed(() => this.modoCriacao() || this.alterando());

  // Versão e data são definidas pelo backend; o status só é editável na alteração.
  readonly form = this.fb.nonNullable.group({
    nome: ['', [obrigatorio, Validators.maxLength(120)]],
    descricao: ['', [obrigatorio, Validators.maxLength(500)]],
    status: ['Ativo' as TermoStatus, Validators.required],
    motivoAlteracao: ['', [obrigatorio, Validators.maxLength(500)]],
  });

  /** Signal para o OnPush reagir às mudanças de validade do formulário. */
  readonly formValido = toSignal(
    this.form.statusChanges.pipe(map((status) => status === 'VALID')),
    { initialValue: this.form.valid },
  );

  /** PDF novo ou, na alteração, o PDF atual do termo. */
  readonly temArquivo = computed(
    () => this.arquivoNovo() !== null || (!this.modoCriacao() && this.termo().arquivo !== null),
  );

  /** Habilita "Criar Termo" e "Salvar Alterações". */
  readonly podeEnviar = computed(() => this.formValido() && this.temArquivo());

  readonly formatDate = formatDateIsoToBr;

  readonly statusOptions: readonly TermoStatus[] = ['Ativo', 'Inativo'];

  readonly historicoPage = signal(1);
  readonly historicoPageSize = HISTORICO_PAGE_SIZE;

  readonly historicoPaginado = computed(() => {
    const inicio = (this.historicoPage() - 1) * HISTORICO_PAGE_SIZE;
    return this.termo().historico.slice(inicio, inicio + HISTORICO_PAGE_SIZE);
  });

  readonly motivoVersaoAtual = computed(() => {
    const termo = this.termo();
    const linha = termo.historico.find(
      (versao) => compararVersoes(versao.versao, termo.versao) === 0,
    );
    return linha?.alteracoes.trim() ?? '';
  });

  /** PDF exibido no quadro: o novo escolhido tem prioridade sobre o atual do termo. */
  readonly arquivoAtual = computed(() => {
    const pendente = this.arquivoNovo();
    if (pendente) {
      const tamanho = formatarTamanhoArquivo(pendente.tamanhoBytes);
      return {
        nome: pendente.nome,
        resumo: this.modoCriacao() ? tamanho : tamanho + ' • Substitui o arquivo atual ao salvar',
        url: pendente.url,
        idVersao: undefined,
      };
    }

    const arquivo = this.termo().arquivo;
    if (!arquivo) {
      return null;
    }

    const tamanho = formatarTamanhoArquivo(arquivo.tamanhoBytes);
    const envio = tamanho + ' • Enviado em ' + formatDateIsoToBr(arquivo.enviadoEm);

    return {
      nome: arquivo.nome,
      resumo: arquivo.enviadoPor ? envio + ' por ' + arquivo.enviadoPor : envio,
      url: arquivo.url,
      idVersao: arquivo.idVersao,
    };
  });

  constructor() {
    // Termo novo no painel volta à visualização, exceto quando só os vínculos mudaram.
    let anterior: Termo | null = null;
    effect(() => {
      const termo = this.termo();
      const soMudaramVinculos =
        !this.modoCriacao() && anterior !== null && mesmosDadosDoPainel(anterior, termo);
      anterior = termo;

      if (soMudaramVinculos) {
        return;
      }

      this.recarregarFormulario(termo);
      this.alterando.set(false);
      this.historicoPage.set(1);
    });
  }

  onHistoricoPageChange(page: number): void {
    this.historicoPage.set(page);
  }

  showControlError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  onAlterar(): void {
    this.recarregarFormulario(this.termo());
    this.alterando.set(true);
  }

  onCancelarAlteracao(): void {
    this.recarregarFormulario(this.termo());
    this.alterando.set(false);
  }

  onArquivoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    // Permite escolher o mesmo arquivo de novo.
    input.value = '';

    if (!arquivo) {
      return;
    }

    if (!this.isPdf(arquivo)) {
      this.arquivoRecusado.emit('O arquivo do termo deve estar no formato PDF.');
      return;
    }

    if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
      this.arquivoRecusado.emit(
        'O arquivo tem ' +
          formatarTamanhoArquivo(arquivo.size) +
          ' e o limite é de ' +
          TAMANHO_MAXIMO_MB +
          'MB.',
      );
      return;
    }

    this.descartarArquivoPendente();
    this.arquivoNovo.set({
      nome: arquivo.name,
      tamanhoBytes: arquivo.size,
      url: URL.createObjectURL(arquivo),
      conteudo: arquivo,
    });
    this.arquivoInvalido.set(false);
  }

  onVisualizarArquivo(): void {
    const arquivo = this.arquivoAtual();

    if (arquivo?.url) {
      this.visualizarArquivo.emit({ nome: arquivo.nome, url: arquivo.url });
    } else if (arquivo?.idVersao) {
      this.visualizarArquivo.emit({ nome: arquivo.nome, idVersao: arquivo.idVersao });
    }
  }

  /** Na alteração remove direto, pois o PDF atual do termo continua valendo. */
  onRemoverArquivo(): void {
    if (this.modoCriacao()) {
      this.removerArquivo.emit();
      return;
    }

    this.descartarArquivoPendente();
  }

  onCancelarCriacao(): void {
    this.cancelarCriacao.emit();
  }

  onSubmit(): void {
    if (this.modoCriacao()) {
      this.onCriar();
      return;
    }

    this.onSalvar();
  }

  onSalvar(): void {
    this.arquivoInvalido.set(!this.temArquivo());

    if (this.form.invalid || !this.temArquivo()) {
      this.form.markAllAsTouched();
      return;
    }

    this.salvar.emit({ ...this.form.getRawValue(), arquivo: this.arquivoNovo() });
    this.form.markAsPristine();
  }

  onCriar(): void {
    const arquivo = this.arquivoNovo();
    this.arquivoInvalido.set(!arquivo);

    if (this.form.invalid || !arquivo) {
      this.form.markAllAsTouched();
      return;
    }

    // Sem status: na criação quem define é o backend.
    const { nome, descricao, motivoAlteracao } = this.form.getRawValue();
    this.criar.emit({ nome, descricao, motivo: motivoAlteracao, arquivo });
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  private recarregarFormulario(termo: Termo): void {
    this.form.reset({
      nome: termo.nome,
      descricao: termo.descricao,
      status: termo.status,
      motivoAlteracao: '',
    });
    this.descartarArquivoPendente();
    this.arquivoInvalido.set(false);
  }

  /**
   * Remove o PDF novo e libera o `blob:` dele. `untracked` porque é chamado
   * dentro do `effect`, que senão rodaria de novo a cada PDF escolhido.
   */
  private descartarArquivoPendente(): void {
    untracked(() => {
      const url = this.arquivoNovo()?.url;

      if (url && url !== this.termo().arquivo?.url) {
        URL.revokeObjectURL(url);
      }

      this.arquivoNovo.set(null);
    });
  }

  private isPdf(arquivo: File): boolean {
    return (
      arquivo.type === 'application/pdf' || arquivo.name.toLowerCase().endsWith('.pdf')
    );
  }
}
